import { Fragment, useCallback, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  ArrowDown,
  ArrowUp,
  ChevronRight,
  ChevronsUpDown,
  CircleCheck,
  CircleX,
  Columns3,
  ExternalLink,
  GripVertical,
  HelpCircle,
  Lock,
  LockOpen,
  Star,
  Clock,
  TriangleAlert,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PaginationControls } from "@/components/ui/PaginationControls/PaginationControls";
import { CLICKS_LIMIT_OPTIONS } from "@/hooks/useClicksData";
import { useIndexationUrlChildren, useTargetTimeline } from "@/hooks/useIndexationData";
import { moneyFormatter } from "@/config/currency";
import { cn } from "@/lib/utils";
import { orderedRules, type SortRule } from "@/components/clicks/clicksGridEngine";
import { CopyableValue } from "@/components/clicks/CopyableValue";
import type { IndexStatus, IndexTargetRow, IndexTimelineEvent } from "@/types/indexation";
import {
  INDEX_COLUMNS,
  INDEX_COLUMN_BY_ID,
  LUMEN_NOTICE_URL,
  rowAlertLevel,
  type IndexColumnDef,
  type IndexGrain,
} from "./indexationColumns";

const CELL_CLASS = "whitespace-nowrap border-r border-border px-4 py-3 last:border-r-0";
const money = moneyFormatter();
const EMPTY_VIP: ReadonlySet<number> = new Set();

// Dates render in the viewer's local timezone; label the columns with the
// current UTC offset so the time is unambiguous.
const TZ_LABEL = `UTC${format(new Date(), "xxx")}`;
const fmtDate = (iso: string | null) => (iso ? format(new Date(iso), "dd.MM.yyyy HH:mm") : "—");
const fmtInt = (n: number | null) => (n == null ? "—" : n.toLocaleString("en-US"));
const fmtMoney = (n: number | null) => (n == null ? "—" : money.format(n));

// m_* source field for each numeric/currency column.
const MONEY_FIELD: Record<string, keyof IndexTargetRow> = {
  clicks: "m_clicks",
  revenue: "m_revenue",
  conversions: "m_conversions",
  registrations: "m_leads",
  deposits: "m_sales",
  money: "m_profit",
};

const INDEX_BADGE: Record<
  "indexed" | "not_indexed" | "unchecked",
  { label: string; className: string; Icon: typeof CircleCheck }
> = {
  indexed: { label: "Indexed", className: "border-transparent bg-emerald-600 text-white", Icon: CircleCheck },
  not_indexed: { label: "Not indexed", className: "border-transparent bg-destructive text-white", Icon: CircleX },
  unchecked: { label: "Not checked", className: "text-muted-foreground", Icon: HelpCircle },
};
const indexKey = (s: IndexStatus) =>
  s === "indexed" ? "indexed" : s === "not_indexed" ? "not_indexed" : "unchecked";

const ALERT_BORDER: Record<"alarm" | "warn", string> = {
  alarm: "border-l-2 border-l-destructive",
  warn: "border-l-2 border-l-amber-500",
};

const headLabel = (col: IndexColumnDef, grain: IndexGrain): string =>
  col.id === "target" ? (grain === "url" ? "URL" : "Domain") : col.label;

export interface IndexationGridProps {
  grain: IndexGrain;
  rows: IndexTargetRow[];
  total: number;
  isPending: boolean;
  isError: boolean;
  isPlaceholder: boolean;
  visibleColumns: string[];
  onVisibleColumnsChange: (cols: string[]) => void;
  /** Active VIP (pinned-override) target ids — marks the VIP column. */
  vipIds?: ReadonlySet<number>;
  /** Range bounding the per-row history drawer (null = unbounded). */
  historyFrom?: string | null;
  historyTo?: string | null;
  /** Sort state (clicksGridEngine SortRule[]); every rule is a server sort key. */
  sortRules: SortRule[];
  /** additive = shift-click (add a key instead of replacing). */
  onHeaderSort: (id: string, additive: boolean) => void;
  onLockSort: (id: string) => void;
  page: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  onPageSizeChange: (n: number) => void;
}

export function IndexationGrid({
  grain,
  rows,
  total,
  isPending,
  isError,
  isPlaceholder,
  visibleColumns,
  onVisibleColumnsChange,
  vipIds,
  historyFrom,
  historyTo,
  sortRules,
  onHeaderSort,
  onLockSort,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: IndexationGridProps) {
  const [openDomains, setOpenDomains] = useState<ReadonlySet<string>>(() => new Set());
  const vip = vipIds ?? EMPTY_VIP;
  const hist = { from: historyFrom ?? null, to: historyTo ?? null };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // The columns actually on screen, in display order. target is pinned first; the
  // rest drag and toggle. An unknown id (stale saved view) is skipped.
  const cols = useMemo(
    () =>
      visibleColumns
        .map((id) => INDEX_COLUMN_BY_ID.get(id))
        .filter((c): c is IndexColumnDef => Boolean(c)),
    [visibleColumns],
  );
  const pinned = cols.find((c) => c.pinned);
  const middle = useMemo(() => cols.filter((c) => !c.pinned), [cols]);
  const colSpan = cols.length;

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const ids = middle.map((c) => c.id);
      const from = ids.indexOf(active.id as string);
      const to = ids.indexOf(over.id as string);
      if (from < 0 || to < 0) return;
      const reordered = arrayMove(ids, from, to);
      onVisibleColumnsChange([...(pinned ? [pinned.id] : []), ...reordered]);
    },
    [middle, pinned, onVisibleColumnsChange],
  );

  const toggleColumn = useCallback(
    (id: string) => {
      if (visibleColumns.includes(id)) {
        onVisibleColumnsChange(visibleColumns.filter((c) => c !== id));
      } else {
        onVisibleColumnsChange([...visibleColumns, id]);
      }
    },
    [visibleColumns, onVisibleColumnsChange],
  );

  const toggleDomain = useCallback((domain: string) => {
    setOpenDomains((cur) => {
      const next = new Set(cur);
      if (!next.delete(domain)) next.add(domain);
      return next;
    });
  }, []);

  const sortFor = (id: string) => sortRules.find((r) => r.id === id);
  // Rank badge only when more than one key is active (keeps a single sort clean).
  const ordered = orderedRules(sortRules);
  const rankOf = (id: string): number | null => {
    if (ordered.length <= 1) return null;
    const i = ordered.findIndex((r) => r.id === id);
    return i >= 0 ? i + 1 : null;
  };

  return (
    <div className="space-y-4">
      {/* Toolbar: row count + freshness + the column picker. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className={cn("text-sm text-muted-foreground", isPlaceholder && "opacity-60")}>
          <span className="font-semibold text-foreground">{total.toLocaleString("en-US")}</span>{" "}
          {grain === "url" ? "URLs" : "domains"}
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium hover:bg-muted"
            >
              <Columns3 className="size-3.5" /> Columns
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56 space-y-1">
            <p className="px-1 pb-1 text-xs font-medium text-muted-foreground">Show columns</p>
            {INDEX_COLUMNS.filter((c) => !c.pinned).map((c) => (
              <label
                key={c.id}
                className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted"
              >
                <input
                  type="checkbox"
                  className="size-3.5 cursor-pointer accent-primary"
                  checked={visibleColumns.includes(c.id)}
                  onChange={() => toggleColumn(c.id)}
                />
                {c.label}
              </label>
            ))}
          </PopoverContent>
        </Popover>
      </div>

      <div className="relative w-full overflow-x-auto rounded-lg border border-border bg-card">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          autoScroll={{ canScroll: (el) => el !== document.scrollingElement }}
        >
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border hover:bg-transparent">
                {pinned ? (
                  <TableHead
                    className={cn(
                      CELL_CLASS,
                      "sticky left-0 z-20 min-w-[280px] bg-muted font-semibold text-foreground",
                    )}
                  >
                    <SortLabel
                      label={headLabel(pinned, grain)}
                      sortable={Boolean(pinned.sort)}
                      rule={sortFor(pinned.id)}
                      rank={rankOf(pinned.id)}
                      onSort={(additive) => onHeaderSort(pinned.id, additive)}
                      onLock={() => onLockSort(pinned.id)}
                    />
                  </TableHead>
                ) : null}

                <SortableContext items={middle.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
                  {middle.map((c) => (
                    <DraggableHead
                      key={c.id}
                      id={c.id}
                      align={c.align}
                      className={cn(CELL_CLASS, "bg-muted font-semibold text-foreground")}
                    >
                      <SortLabel
                        label={headLabel(c, grain)}
                        sortable={Boolean(c.sort)}
                        rule={sortFor(c.id)}
                        rank={rankOf(c.id)}
                        onSort={(additive) => c.sort && onHeaderSort(c.id, additive)}
                        onLock={() => onLockSort(c.id)}
                      />
                    </DraggableHead>
                  ))}
                </SortableContext>
              </TableRow>
            </TableHeader>

            <TableBody>
              {isError ? (
                <Message colSpan={colSpan} tone="error">
                  Could not load indexation data. Reload or narrow the filters.
                </Message>
              ) : isPending ? (
                <Message colSpan={colSpan}>Loading…</Message>
              ) : rows.length === 0 ? (
                <Message colSpan={colSpan}>No targets match these filters.</Message>
              ) : (
                rows.map((r) => {
                  const expandable = grain === "domain";
                  const open = expandable && openDomains.has(r.domain);
                  return (
                    <Fragment key={r.id}>
                      <IndexationRow
                        row={r}
                        cols={cols}
                        vipIds={vip}
                        hist={hist}
                        expandable={expandable}
                        open={open}
                        onToggle={() => toggleDomain(r.domain)}
                      />
                      {open ? (
                        <UrlChildren domain={r.domain} cols={cols} colSpan={colSpan} vipIds={vip} hist={hist} />
                      ) : null}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </DndContext>
      </div>

      {total > 0 ? (
        <div className={cn("flex items-center justify-between gap-4", isPlaceholder && "opacity-60")}>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Showing</span>
            <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
              <SelectTrigger className="h-9 w-[80px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CLICKS_LIMIT_OPTIONS.map((o) => (
                  <SelectItem key={o} value={String(o)}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">per page</span>
          </div>
          <PaginationControls currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
        </div>
      ) : null}
    </div>
  );
}

function Message({
  colSpan,
  tone,
  children,
}: {
  colSpan: number;
  tone?: "error";
  children: React.ReactNode;
}) {
  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        className={cn("py-10 text-center text-sm", tone === "error" ? "text-destructive" : "text-muted-foreground")}
      >
        {children}
      </TableCell>
    </TableRow>
  );
}

/** Header label + sort arrow + (when multi-key) rank + a lock toggle. Click
 *  sorts; shift-click adds a secondary key; the lock pins a key ahead of the
 *  rest — same engine as the Clicks grid. Shared by pinned and draggable heads. */
function SortLabel({
  label,
  sortable,
  rule,
  rank,
  onSort,
  onLock,
}: {
  label: string;
  sortable: boolean;
  rule: SortRule | undefined;
  rank: number | null;
  onSort: (additive: boolean) => void;
  onLock: () => void;
}) {
  if (!sortable) return <span className="truncate">{label}</span>;
  return (
    <span className="inline-flex min-w-0 items-center gap-0.5">
      <button
        type="button"
        onClick={(e) => onSort(e.shiftKey)}
        title="Click to sort · Shift-click to add a key (server-side)"
        className={cn(
          "inline-flex min-w-0 cursor-pointer items-center gap-0.5 hover:text-foreground",
          rule ? "text-foreground" : "text-muted-foreground",
        )}
      >
        <span className="truncate">{label}</span>
        {rule ? (
          rule.desc ? <ArrowDown className="size-3.5 shrink-0" /> : <ArrowUp className="size-3.5 shrink-0" />
        ) : (
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-30" />
        )}
        {rank != null ? <span className="text-[10px] tabular-nums text-muted-foreground">{rank}</span> : null}
      </button>
      {rule ? (
        <button
          type="button"
          onClick={onLock}
          title={rule.locked ? "Unlock sort key" : "Lock as sort key"}
          aria-label={rule.locked ? "Unlock sort key" : "Lock as sort key"}
          className="cursor-pointer shrink-0"
        >
          {rule.locked ? (
            <Lock className="size-3 text-primary" />
          ) : (
            <LockOpen className="size-3 text-muted-foreground/40 hover:text-foreground" />
          )}
        </button>
      ) : null}
    </span>
  );
}

/** A draggable (reorderable) header cell; the grip carries the drag listeners so
 *  the label keeps its own sort click. Copied idiom from ClicksGrid. */
function DraggableHead({
  id,
  align,
  className,
  children,
}: {
  id: string;
  align?: "right";
  className?: string;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 20 : undefined,
  };
  return (
    <TableHead ref={setNodeRef} style={style} className={cn(className, align === "right" && "text-right")}>
      <span className={cn("inline-flex items-center gap-1", align === "right" && "flex-row-reverse")}>
        <button
          type="button"
          {...attributes}
          {...listeners}
          title="Drag to reorder column"
          aria-label="Drag to reorder column"
          className="cursor-grab touch-none text-muted-foreground/40 hover:text-foreground active:cursor-grabbing"
        >
          <GripVertical className="size-3.5" />
        </button>
        {children}
      </span>
    </TableHead>
  );
}

/** One data cell, rendered by the column's format tag. */
function Cell({ col, row, isVip }: { col: IndexColumnDef; row: IndexTargetRow; isVip: boolean }) {
  switch (col.format) {
    case "brand":
      return <TableCell className={cn(CELL_CLASS, "text-muted-foreground")}>{row.brand ?? "—"}</TableCell>;
    case "vip":
      return (
        <TableCell className={CELL_CLASS}>
          {isVip ? (
            <span className="inline-flex items-center gap-1 text-amber-500" title="VIP — pinned to a frequent re-check">
              <Star className="size-3.5 fill-current" /> VIP
            </span>
          ) : (
            <span className="text-muted-foreground/60">—</span>
          )}
        </TableCell>
      );
    case "index_status": {
      const b = INDEX_BADGE[indexKey(row.index_status)];
      return (
        <TableCell className={CELL_CLASS}>
          <Badge variant="outline" className={b.className}>
            <b.Icon className="size-3" /> {b.label}
          </Badge>
        </TableCell>
      );
    }
    case "dmca_status":
      return (
        <TableCell className={CELL_CLASS}>
          <div className="flex items-center gap-1.5">
            {row.last_status === "dmca" ? (
              <Badge variant="destructive">DMCA</Badge>
            ) : row.last_status === "clean" ? (
              <Badge variant="secondary" className="border-transparent bg-emerald-600 text-white">Clean</Badge>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
            {row.error_streak > 0 && row.last_status != null ? (
              <TriangleAlert
                className="size-3.5 text-amber-500"
                aria-label="Last check failed — showing the previous verdict"
              />
            ) : null}
          </div>
        </TableCell>
      );
    case "tier":
      return <TableCell className={cn(CELL_CLASS, "text-muted-foreground")}>T{row.tier}</TableCell>;
    case "int":
      return (
        <TableCell className={cn(CELL_CLASS, "text-right tabular-nums")}>
          {fmtInt(row[MONEY_FIELD[col.id]] as number | null)}
        </TableCell>
      );
    case "currency":
      return (
        <TableCell className={cn(CELL_CLASS, "text-right tabular-nums")}>
          {fmtMoney(row[MONEY_FIELD[col.id]] as number | null)}
        </TableCell>
      );
    case "notices":
      return (
        <TableCell className={cn(CELL_CLASS, "text-right tabular-nums")}>{row.last_notices ?? "—"}</TableCell>
      );
    case "lumen":
      return (
        <TableCell className={cn(CELL_CLASS, "text-right")}>
          {row.last_notice_ids.length ? (
            <a
              href={LUMEN_NOTICE_URL(row.last_notice_ids[0])}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-[var(--chart-2)] hover:underline"
            >
              {row.last_notice_ids.length} notice{row.last_notice_ids.length > 1 ? "s" : ""}
              <ExternalLink className="size-3" />
            </a>
          ) : (
            <span className="text-muted-foreground/60">—</span>
          )}
        </TableCell>
      );
    case "date": {
      const iso =
        col.id === "flagged_since" ? row.flagged_since
        : col.id === "dropped_since" ? row.index_dropped_since
        : row.index_checked_at;
      return <TableCell className={cn(CELL_CLASS, "text-muted-foreground")}>{fmtDate(iso)}</TableCell>;
    }
    case "url_removed":
      return (
        <TableCell className={CELL_CLASS}>
          {row.url_present === false ? (
            <span className="text-xs font-medium text-destructive" title="Notice present and the page is gone from results">
              removed
            </span>
          ) : (
            <span className="text-muted-foreground/60">—</span>
          )}
        </TableCell>
      );
    default:
      return <TableCell className={CELL_CLASS} />;
  }
}

/** The pinned identity cell: domain/url with an expand chevron on the domain grain
 *  and a history (clock) button that opens the transition timeline. */
function TargetCell({
  row,
  hist,
  expandable,
  open,
  indent,
}: {
  row: IndexTargetRow;
  hist: { from: string | null; to: string | null };
  expandable?: boolean;
  open?: boolean;
  indent?: boolean;
}) {
  return (
    <TableCell
      className={cn(CELL_CLASS, "sticky left-0 z-10 bg-card font-medium", open && "bg-muted/40")}
    >
      <div className={cn("flex items-center gap-1.5", indent && "pl-6")}>
        {expandable ? (
          <ChevronRight
            className={cn("size-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-90")}
            aria-hidden
          />
        ) : null}
        <CopyableValue value={row.target} noTruncate className={cn(indent && "text-muted-foreground")} />
        <HistoryButton row={row} from={hist.from} to={hist.to} />
      </div>
    </TableCell>
  );
}

/** Clock button → a dialog with the target's index/DMCA transition timeline.
 *  Lazy: the timeline query only runs while the dialog is open. */
function HistoryButton({
  row,
  from,
  to,
}: {
  row: IndexTargetRow;
  from: string | null;
  to: string | null;
}) {
  const [open, setOpen] = useState(false);
  const { data, isPending, isError } = useTargetTimeline(row.id, from, to, open);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          title="История индексации и DMCA"
          aria-label="История индексации и DMCA"
          className="ml-0.5 shrink-0 cursor-pointer text-muted-foreground/50 hover:text-foreground"
        >
          <Clock className="size-3.5" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="truncate">{row.target} · history</DialogTitle>
        </DialogHeader>
        <Timeline
          kind={row.kind}
          events={data ?? []}
          isPending={isPending}
          isError={isError}
        />
      </DialogContent>
    </Dialog>
  );
}

/** Index/DMCA transition timeline, newest first. The RPC already collapses runs
 *  to change-points (ascending); we diff consecutive points to label what
 *  changed, then reverse for display. */
function Timeline({
  kind,
  events,
  isPending,
  isError,
}: {
  kind: IndexTargetRow["kind"];
  events: IndexTimelineEvent[];
  isPending: boolean;
  isError: boolean;
}) {
  if (isPending) return <p className="py-6 text-sm text-muted-foreground">Загрузка истории…</p>;
  if (isError) return <p className="py-6 text-sm text-destructive">Не удалось загрузить историю.</p>;
  if (events.length === 0)
    return <p className="py-6 text-sm text-muted-foreground">Нет проверок в выбранном диапазоне.</p>;

  // Build labelled items by diffing consecutive change-points (ascending input).
  type Item = { at: string; dot: string; text: string; noticeIds: string[] };
  const items: Item[] = [];
  let prevIdx: boolean | null | undefined;
  let prevDmca: "dmca" | "clean" | null | undefined;
  events.forEach((e, i) => {
    const first = i === 0;
    if (e.indexed !== prevIdx) {
      items.push({
        at: e.at,
        dot: e.indexed === false ? "d-red" : "d-green",
        text: e.indexed === false ? "Выпал из индекса" : first ? "В индексе (начало наблюдения)" : "Вернулся в индекс",
        noticeIds: [],
      });
    }
    if (e.dmca !== prevDmca && e.dmca != null) {
      items.push({
        at: e.at,
        dot: e.dmca === "dmca" ? "d-amber" : "d-grey",
        text: e.dmca === "dmca" ? "Прилетела DMCA" : "DMCA снята",
        noticeIds: e.dmca === "dmca" ? e.notice_ids : [],
      });
    }
    prevIdx = e.indexed;
    prevDmca = e.dmca;
  });
  void kind;
  items.reverse();

  const dotClass: Record<string, string> = {
    "d-red": "bg-destructive",
    "d-green": "bg-emerald-600",
    "d-amber": "bg-amber-500",
    "d-grey": "bg-muted-foreground",
  };
  return (
    <ul className="max-h-[60vh] space-y-3 overflow-y-auto py-1">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3 text-sm">
          <span className={cn("mt-1.5 size-2.5 shrink-0 rounded-full", dotClass[it.dot])} />
          <div className="min-w-0">
            <div className="font-medium">{it.text}</div>
            <div className="text-xs text-muted-foreground">{fmtDate(it.at)} ({TZ_LABEL})</div>
            {it.noticeIds.length ? (
              <a
                href={LUMEN_NOTICE_URL(it.noticeIds[0])}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-[var(--chart-2)] hover:underline"
              >
                Lumen #{it.noticeIds[0]} <ExternalLink className="size-3" />
              </a>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function IndexationRow({
  row,
  cols,
  vipIds,
  hist,
  expandable,
  open,
  onToggle,
}: {
  row: IndexTargetRow;
  cols: IndexColumnDef[];
  vipIds: ReadonlySet<number>;
  hist: { from: string | null; to: string | null };
  expandable: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const alert = rowAlertLevel(row);
  const isVip = vipIds.has(row.id);
  return (
    <TableRow
      className={cn(alert && ALERT_BORDER[alert], expandable && "cursor-pointer")}
      onClick={expandable ? onToggle : undefined}
    >
      {cols.map((c) =>
        c.pinned ? (
          <TargetCell key={c.id} row={row} hist={hist} expandable={expandable} open={open} />
        ) : (
          <Cell key={c.id} col={c} row={row} isVip={isVip} />
        ),
      )}
    </TableRow>
  );
}

/** Lazy url children of an expanded domain — same columns, exact m_* per url. */
function UrlChildren({
  domain,
  cols,
  colSpan,
  vipIds,
  hist,
}: {
  domain: string;
  cols: IndexColumnDef[];
  colSpan: number;
  vipIds: ReadonlySet<number>;
  hist: { from: string | null; to: string | null };
}) {
  const { data, isPending, isError } = useIndexationUrlChildren(domain, true);

  if (isPending || isError || !data || data.length === 0) {
    return (
      <TableRow className="bg-muted/30 hover:bg-muted/30">
        <TableCell colSpan={colSpan} className="py-3 pl-10 text-sm text-muted-foreground">
          {isPending ? "Loading URLs…" : isError ? "Could not load URLs." : "No tracked URLs for this domain yet."}
        </TableCell>
      </TableRow>
    );
  }
  return (
    <>
      {data.map((u) => (
        <TableRow key={u.id} className="border-b border-border/50 bg-muted/20 hover:bg-muted/40">
          {cols.map((c) =>
            c.pinned ? (
              <TargetCell key={c.id} row={u} hist={hist} indent />
            ) : (
              <Cell key={c.id} col={c} row={u} isVip={vipIds.has(u.id)} />
            ),
          )}
        </TableRow>
      ))}
    </>
  );
}
